'use client';

import { type ComponentProps, type ReactNode, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Pagination } from '@zillow/constellation-10';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { css } from '@/styled-system/css';

// Constellation's renderItem callback declares `buttonProps` as
// `Partial<PaginationPageButtonPropsInterface>`, but its JSX-type inference
// for `<Pagination.PageButton>` narrows to plain `RefAttributes` when a spread
// is involved — making `<Pagination.PageButton {...buttonProps} />` error
// even though the destination component does accept those props at runtime.
// Still broken on 10.18.1 (LegacyRef vs Ref mismatch). Cast to the component's
// `ComponentProps` so the spread satisfies the narrow inferred type without
// `@ts-expect-error`.
type PageButtonProps = ComponentProps<typeof Pagination.PageButton>;

import type { SearchListingsRequest, SearchListingsResult } from '@/src/lib/api';
import { Link as NextLink } from '@/src/i18n/navigation';
import { LISTINGS_PER_PAGE } from '@/src/lib/constants';
import { useSearchListings } from '@/src/lib/hooks/query';
import { emitTrackNextClickExplorePageEvent } from '@/src/analytics/hotpads/events';
import { buildSrpPaginationHref } from '@/src/lib/navigation/srpQueryCarryover';
import { getSrpPageFromUrl, type QueryValue } from '@/src/lib/seo/srp-pagination';
import { extractV3PaginationCounts } from '@/src/lib/utils/v3Listings';
import { useSrpLiveSearchRequest } from './useSrpLiveSearchRequest';
import { useSrpViewMode } from './ViewModeContext';

type Props = {
  breadcrumbs?: ReactNode;
  currentPage: number;
  initialListingsData: SearchListingsResult | null;
  initialParams: SearchListingsRequest;
  areaId: string;
  pathname: string;
  paginationQuery: Record<string, QueryValue>;
};

// Fallback for browsers without `inert`, which does the real work of keeping stale
// page links unreachable while the block holds its box.
const inertPagination = css({ pointerEvents: 'none' });

// Scroll the nearest scrollable ancestor (e.g., the content panel) to top on page change
function scrollNearestScrollableToTop(el: HTMLElement | null) {
  while (el) {
    if (el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'visible') {
      el.scrollTo({ top: 0 });
      return;
    }
    el = el.parentElement;
  }
  window.scrollTo({ top: 0 });
}

/**
 * SRP footer pagination. Filter/map changes update the URL via pushState (no
 * server rerender), so the totals can't be static SSR props — this reads the
 * same live V3 search query LiveListingsGrid drives and derives page counts
 * from its result (HP-8594).
 */
export function ListingsFooter({
  breadcrumbs,
  currentPage,
  initialListingsData,
  initialParams,
  areaId,
  pathname,
  paginationQuery,
}: Props) {
  const tCommon = useTranslations('common');
  const { viewMode } = useSrpViewMode();
  const livePathname = usePathname() ?? pathname;
  const searchParams = useSearchParams();
  const resolvedCurrentPage = getSrpPageFromUrl(livePathname, searchParams?.get('page') ?? undefined);

  const liveParams = useSrpLiveSearchRequest({ initialParams, areaId, page: currentPage });
  const isInitialParams = liveParams === initialParams;
  const { data, isPlaceholderData, fetchStatus } = useSearchListings(liveParams, {
    refetchOnMount: false,
    initialData: isInitialParams ? (initialListingsData ?? undefined) : undefined,
  });
  // Render the placeholder result's totals rather than dropping to zero: unmounting
  // the block moved the area content below it twice per map pan (HP-8727). It still
  // unmounts when a search settles at zero, which the reserved empty region absorbs.
  // `fetchStatus` matters: a query disabled for want of a finite bounding box keeps
  // serving placeholder data forever, which would leave the block inert permanently.
  const isPaginationStale = !isInitialParams && isPlaceholderData && fetchStatus !== 'idle';
  const effectiveData = isInitialParams ? (data ?? initialListingsData ?? undefined) : (data ?? undefined);
  const { totalListingsAvailable, totalPages } = extractV3PaginationCounts(
    effectiveData?.listings,
    LISTINGS_PER_PAGE,
    livePathname,
  );

  // livePathname, not the SSR prop: after a client-side area rewrite
  // (useDetectedAreaUrlSync) the prop is frozen on the old area.
  const buildPageUrl = useCallback(
    (page: number): string => buildSrpPaginationHref(livePathname, page, searchParams, paginationQuery),
    [paginationQuery, livePathname, searchParams],
  );

  const handlePageLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    scrollNearestScrollableToTop(e.currentTarget.parentElement);
  }, []);

  const handleNextPageClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      emitTrackNextClickExplorePageEvent();
      handlePageLinkClick(e);
    },
    [handlePageLinkClick],
  );

  if (viewMode === 'feed') return null;

  // Only gate our own handlers at the boundaries — aria-disabled, focusability,
  // and click suppression on the anchors are Constellation's (it derives the
  // disabled state from currentPageIndex/totalPages and deliberately keeps
  // disabled links focusable).
  const atFirstPage = resolvedCurrentPage <= 1;
  const atLastPage = resolvedCurrentPage >= totalPages;

  return (
    <>
      {breadcrumbs ? (
        <div
          id="srp-breadcrumbs"
          data-srp-component="breadcrumbs"
          className={css({
            display: 'flex',
            justifyContent: 'center',
            marginTop: 'default',
            marginBottom: 'tight',
          })}
        >
          {breadcrumbs}
        </div>
      ) : null}

      {totalListingsAvailable > 0 && (
        <div
          id="srp-pagination"
          data-srp-component="pagination"
          aria-busy={isPaginationStale ? 'true' : undefined}
          className={isPaginationStale ? inertPagination : undefined}
          {...(isPaginationStale ? { inert: '' } : {})}
        >
          <Pagination.Root
            currentPageIndex={Math.max(0, resolvedCurrentPage - 1)}
            totalPages={totalPages}
            // Below lg the numbered window collapses (via Constellation's CSS
            // variant, so the links stay in the SSR HTML for crawlers) and
            // first/last render as the jump-chevron row instead.
            showNumberButtons={{ base: false, lg: true }}
            // First/last stay linked in the SSR HTML so crawlers reach deep
            // pages in a few hops instead of a linear walk through the chain.
            showFirstLastButtons
            onPageSelected={() => {
              /* no-op: navigation is driven by the rendered <Link> hrefs */
            }}
            aria-label={tCommon('pagination')}
            data-testid="pagination"
          >
            <Pagination.List>
              <Pagination.Item>
                {/* asChild swaps in our link as the slotted child, whose (empty)
                    children replace the button's default glyph — the `icon` prop
                    is the fallback Constellation renders (inside its own <Icon>,
                    hence asRender) in that case. */}
                <Pagination.JumpToFirstButton
                  asChild
                  icon={<LucideIcon asRender icon={ChevronFirst} />}
                  title={tCommon('goToFirstPage')}
                >
                  <NextLink
                    href={buildPageUrl(1)}
                    prefetch={false}
                    rel={undefined}
                    onClick={atFirstPage ? undefined : handlePageLinkClick}
                  />
                </Pagination.JumpToFirstButton>
              </Pagination.Item>
              <Pagination.Item>
                <Pagination.PreviousButton
                  asChild
                  icon={<LucideIcon asRender icon={ChevronLeft} />}
                  title={tCommon('goToPreviousPage')}
                >
                  <NextLink
                    href={buildPageUrl(Math.max(1, resolvedCurrentPage - 1))}
                    prefetch={false}
                    rel={undefined}
                    onClick={atFirstPage ? undefined : handlePageLinkClick}
                  />
                </Pagination.PreviousButton>
              </Pagination.Item>
              <Pagination.PageItems
                renderItem={({ buttonProps, key, pageIndex }) => {
                  const pageNum = pageIndex + 1;
                  const isCurrent = pageNum === resolvedCurrentPage;
                  return (
                    <Pagination.Item key={key}>
                      {isCurrent ? (
                        <Pagination.PageButton
                          {...(buttonProps as PageButtonProps)}
                          selected
                          aria-current="page"
                          aria-label={tCommon('goToPage', { page: pageNum })}
                        >
                          {pageNum}
                        </Pagination.PageButton>
                      ) : (
                        <Pagination.PageButton asChild {...(buttonProps as PageButtonProps)} selected={false}>
                          <NextLink
                            href={buildPageUrl(pageNum)}
                            aria-label={tCommon('goToPage', { page: pageNum })}
                            onClick={handlePageLinkClick}
                            prefetch={false}
                          >
                            {pageNum}
                          </NextLink>
                        </Pagination.PageButton>
                      )}
                    </Pagination.Item>
                  );
                }}
              />
              <Pagination.Readout>
                {tCommon('pageOf', { current: resolvedCurrentPage, total: totalPages })}
              </Pagination.Readout>
              <Pagination.Item>
                <Pagination.NextButton
                  asChild
                  icon={<LucideIcon asRender icon={ChevronRight} />}
                  title={tCommon('goToNextPage')}
                >
                  <NextLink
                    data-testid="link-go-to-next-page"
                    href={buildPageUrl(Math.min(totalPages, resolvedCurrentPage + 1))}
                    prefetch={false}
                    rel={undefined}
                    onClick={atLastPage ? undefined : handleNextPageClick}
                  />
                </Pagination.NextButton>
              </Pagination.Item>
              <Pagination.Item>
                <Pagination.JumpToLastButton
                  asChild
                  icon={<LucideIcon asRender icon={ChevronLast} />}
                  title={tCommon('goToLastPage')}
                >
                  <NextLink
                    href={buildPageUrl(totalPages)}
                    prefetch={false}
                    rel={undefined}
                    onClick={atLastPage ? undefined : handlePageLinkClick}
                  />
                </Pagination.JumpToLastButton>
              </Pagination.Item>
            </Pagination.List>
          </Pagination.Root>
        </div>
      )}
    </>
  );
}
